// 文件上传状态枚举
enum UploadStatus {
  PENDING = 'pending', // 等待上传
  UPLOADING = 'uploading', // 正在上传
  CANCELED = 'canceled', // 取消上传
  PAUSED = 'paused', // 已暂停
  COMPLETED = 'completed', // 上传完成
  FAILED = 'failed', // 上传失败
}

// 单个文件的上传信息
export interface FileUploadInfo {
  fileID: string; // 文件唯一标识符
  file: File; // 文件对象
  status: UploadStatus; // 文件上传状态
  progress: number; // 上传进度百分比 (0-100)
  uploadedSize: number; // 已上传的字节数
  totalSize: number; // 文件总字节数
  error?: 'cancel' | 'timeout'; // 错误类型：取消或超时
  retryCount: number; // 重试次数
}

// 上传配置选项
interface UploadOptions {
  maxConcurrentUploads?: number; // 最大并发上传数，控制同时上传的文件数量
  chunkSize?: number; // 分片大小（字节），用于大文件分片上传
  maxRetries?: number; // 最大重试次数，上传失败后的重试次数
  retryDelay?: number; // 重试延迟（毫秒），两次重试之间的等待时间
  onProgress?: (progress: number) => void; // 总进度回调函数
  onFileProgress?: (fileId: string, progress: number) => void; // 单文件进度回调函数
  onFileComplete?: (fileId: string) => void; // 单文件完成回调函数
  onFileError?: (fileId: string, error: 'cancel' | 'timeout') => void; // 单文件错误回调函数
}

import type { AxiosProgressEvent } from 'axios';

// 导入 API 服务
import { FServiceAPI } from '@freelog/tools-lib';

interface IBatchFileUploadManager {
  addFiles: (files: { fileID: string; file: File }[]) => void;
  pauseUpload: (fileID: string) => void;
  pauseAll: () => void;
  resumeUpload: (fileID: string) => void;
  resumeAll: () => void;
  cancelUpload: (fileID: string) => void;
  cancelAll: () => void;
  retryUpload: (fileID: string) => void;
  getUploadStatus: (fileID: string) => FileUploadInfo | undefined;
  getTotalProgress: () => number;
  clearCompleted: () => void;
  getQueueStatus: () => Map<string, FileUploadInfo>;
  addListenerQueueStatus: (
    listener: (allFileUploadInfo: FileUploadInfo[]) => void,
  ) => void;
  removeListenerQueueStatus: (
    listener: (allFileUploadInfo: FileUploadInfo[]) => void,
  ) => void;
  removeAllListenerQueueStatus: () => void;
  addListenerUploadStatusSuccess: (
    listener: (allFileUploadInfo: FileUploadInfo[]) => void,
  ) => void;
  removeListenerUploadStatusSuccess: (
    listener: (allFileUploadInfo: FileUploadInfo[]) => void,
  ) => void;
  removeAllListenerUploadStatusSuccess: () => void;
  configure: (options: Partial<UploadOptions>) => void;
  clearAll: () => void;
}

class BatchFileUploadManager implements IBatchFileUploadManager {
  // 单例实例
  private static _instance: BatchFileUploadManager;

  // 上传队列，存储所有待上传、上传中和已完成/失败的文件信息
  private _uploadQueue: Map<string, FileUploadInfo>;

  // 当前正在上传的文件ID集合，用于控制并发上传数量
  private _activeUploads: Set<string>;

  // 上传配置选项，包含并发数、分片大小、重试策略等
  private _options: UploadOptions;

  // 队列状态监听器集合，当队列状态变化时通知这些监听器
  private _queueStatusListeners: Set<
    (allFileUploadInfo: FileUploadInfo[]) => void
  >;

  // 上传成功监听器集合，当文件上传成功时通知这些监听器
  private _uploadSuccessListeners: Set<
    (allFileUploadInfo: FileUploadInfo[]) => void
  >;

  // 中止控制器集合，用于取消正在进行的上传
  private _abortControllers: Map<string, AbortController>;

  // 队列状态更新节流定时器，防止频繁触发状态更新
  private _queueUpdateThrottleTimeout: NodeJS.Timeout | null;

  // 上传成功通知防抖定时器，防止频繁触发成功回调
  private _uploadSuccessDebounceTimeout: NodeJS.Timeout | null;

  // 用于跟踪文件状态变化的Map
  private _fileStatusChanges: Map<string, FileUploadInfo>;

  constructor() {
    this._uploadQueue = new Map();
    this._activeUploads = new Set();
    this._queueStatusListeners = new Set();
    this._uploadSuccessListeners = new Set();
    this._abortControllers = new Map();
    this._queueUpdateThrottleTimeout = null;
    this._uploadSuccessDebounceTimeout = null;
    this._fileStatusChanges = new Map();
    this._options = {
      maxConcurrentUploads: 3,
      chunkSize: 1024 * 1024, // 1MB
      maxRetries: 3,
      retryDelay: 1000,
    };
  }

  public static getInstance(): BatchFileUploadManager {
    if (!BatchFileUploadManager._instance) {
      BatchFileUploadManager._instance = new BatchFileUploadManager();
    }
    return BatchFileUploadManager._instance;
  }

  // 配置上传选项
  public configure(options: Partial<UploadOptions>): void {
    this._options = { ...this._options, ...options };
  }

  // 添加文件到上传队列，添加到队列后，排队自动开始上传
  public addFiles(files: { fileID: string; file: File }[]): void {
    files.forEach(({ fileID, file }) => {
      // 检查文件是否已存在
      const existingFile = this._uploadQueue.get(fileID);
      if (existingFile) {
        // 如果文件已存在且未完成，继续之前的状态
        if (existingFile.status !== UploadStatus.COMPLETED) {
          this._fileStatusChanges.set(fileID, { ...existingFile });
          // 如果文件之前是暂停状态，恢复上传
          if (existingFile.status === UploadStatus.PAUSED) {
            this.resumeUpload(fileID);
          }
        }
        return;
      }

      // 如果文件不存在，创建新的上传信息
      const fileInfo: FileUploadInfo = {
        fileID,
        file,
        status: UploadStatus.PENDING,
        progress: 0,
        uploadedSize: 0,
        totalSize: file.size,
        retryCount: 0,
      };
      this._uploadQueue.set(fileID, fileInfo);
      this._fileStatusChanges.set(fileID, { ...fileInfo });
    });

    // 只通知新增的文件
    const changedFiles = Array.from(this._fileStatusChanges.values());
    this._queueStatusListeners.forEach((listener) => listener(changedFiles));
    this._options.onProgress?.(this.getTotalProgress());
    this._fileStatusChanges.clear();

    this._processQueue();
  }

  // 暂停特定文件的上传
  public pauseUpload(fileID: string): void {
    const fileInfo = this._uploadQueue.get(fileID);
    if (fileInfo && fileInfo.status === UploadStatus.UPLOADING) {
      fileInfo.status = UploadStatus.PAUSED;
      this._abortControllers.get(fileID)?.abort();
      this._activeUploads.delete(fileID);
      this._fileStatusChanges.set(fileID, { ...fileInfo });

      // 只通知状态变化的文件
      const changedFiles = Array.from(this._fileStatusChanges.values());
      this._queueStatusListeners.forEach((listener) => listener(changedFiles));
      this._options.onProgress?.(this.getTotalProgress());
      this._fileStatusChanges.clear();

      this._processQueue();
    }
  }

  // 暂停所有上传
  public pauseAll(): void {
    this._uploadQueue.forEach((fileInfo) => {
      if (fileInfo.status === UploadStatus.UPLOADING) {
        this.pauseUpload(fileInfo.fileID);
      }
    });
  }

  // 恢复特定文件的上传
  public resumeUpload(fileID: string): void {
    const fileInfo = this._uploadQueue.get(fileID);
    if (fileInfo && fileInfo.status === UploadStatus.PAUSED) {
      fileInfo.status = UploadStatus.PENDING;
      this._fileStatusChanges.set(fileID, { ...fileInfo });

      // 只通知状态变化的文件
      const changedFiles = Array.from(this._fileStatusChanges.values());
      this._queueStatusListeners.forEach((listener) => listener(changedFiles));
      this._options.onProgress?.(this.getTotalProgress());
      this._fileStatusChanges.clear();

      this._processQueue();
    }
  }

  // 恢复所有上传
  public resumeAll(): void {
    this._uploadQueue.forEach((fileInfo) => {
      if (fileInfo.status === UploadStatus.PAUSED) {
        this.resumeUpload(fileInfo.fileID);
      }
    });
  }

  // 取消特定文件的上传
  public cancelUpload(fileID: string): void {
    const fileInfo = this._uploadQueue.get(fileID);
    if (fileInfo) {
      fileInfo.status = UploadStatus.FAILED;
      fileInfo.error = 'cancel';
      this._abortControllers.get(fileID)?.abort();
      this._activeUploads.delete(fileID);
      this._fileStatusChanges.set(fileID, { ...fileInfo });

      // 只通知状态变化的文件
      const changedFiles = Array.from(this._fileStatusChanges.values());
      this._queueStatusListeners.forEach((listener) => listener(changedFiles));
      this._options.onProgress?.(this.getTotalProgress());
      this._fileStatusChanges.clear();

      this._processQueue();
    }
  }

  // 取消所有上传
  public cancelAll(): void {
    // 取消所有正在进行的上传
    this._abortControllers.forEach((controller) => {
      controller.abort();
    });
    this._abortControllers.clear();
    this._activeUploads.clear();
    this._uploadQueue.clear();
    this._fileStatusChanges.clear();
    this.notifyQueueStatusChange();
  }

  /**
   * 清除所有上传和监听
   * 用于组件卸载时清理资源
   */
  public clearAll(): void {
    // 取消所有上传
    this.cancelAll();

    // 清除所有监听器
    this._queueStatusListeners.clear();
    this._uploadSuccessListeners.clear();

    // 重置状态
    this._fileStatusChanges.clear();
    this._activeUploads.clear();
    this._abortControllers.clear();
    this._uploadQueue.clear();

  }

  public clearAllData(): void {
     // 取消所有上传
     this.cancelAll();
      // 重置状态
    this._fileStatusChanges.clear();
    this._activeUploads.clear();
    this._abortControllers.clear();
    this._uploadQueue.clear();

  }

  // 重试失败的上传
  public retryUpload(fileID: string): void {
    const fileInfo = this._uploadQueue.get(fileID);
    if (fileInfo && fileInfo.status === UploadStatus.FAILED) {
      fileInfo.status = UploadStatus.PENDING;
      fileInfo.progress = 0;
      fileInfo.uploadedSize = 0;
      fileInfo.error = undefined;
      this._fileStatusChanges.set(fileID, { ...fileInfo });

      // 只通知状态变化的文件
      const changedFiles = Array.from(this._fileStatusChanges.values());
      this._queueStatusListeners.forEach((listener) => listener(changedFiles));
      this._options.onProgress?.(this.getTotalProgress());
      this._fileStatusChanges.clear();

      this._processQueue();
    }
  }

  // 获取上传状态
  public getUploadStatus(fileID: string): FileUploadInfo | undefined {
    return this._uploadQueue.get(fileID);
  }

  // 获取总体上传进度
  public getTotalProgress(): number {
    let totalSize = 0;
    let totalUploaded = 0;
    this._uploadQueue.forEach((fileInfo) => {
      totalSize += fileInfo.totalSize;
      totalUploaded += fileInfo.uploadedSize;
    });
    return totalSize === 0 ? 0 : (totalUploaded / totalSize) * 100;
  }

  // 清理已完成的上传
  public clearCompleted(): void {
    this._uploadQueue.forEach((fileInfo, fileID) => {
      if (fileInfo.status === UploadStatus.COMPLETED) {
        this._uploadQueue.delete(fileID);
        this._fileStatusChanges.set(fileID, {
          ...fileInfo,
          status: UploadStatus.COMPLETED,
        });
      }
    });

    // 只通知被删除的文件
    const changedFiles = Array.from(this._fileStatusChanges.values());
    this._queueStatusListeners.forEach((listener) => listener(changedFiles));
    this._options.onProgress?.(this.getTotalProgress());
    this._fileStatusChanges.clear();
  }

  // 获取当前队列中的所有文件信息
  public getQueueStatus(): Map<string, FileUploadInfo> {
    return new Map(this._uploadQueue);
  }

  // 添加队列状态监听器
  public addListenerQueueStatus(
    listener: (allFileUploadInfo: FileUploadInfo[]) => void,
  ): void {
    this._queueStatusListeners.add(listener);
  }

  // 移除队列状态监听器
  public removeListenerQueueStatus(
    listener: (allFileUploadInfo: FileUploadInfo[]) => void,
  ): void {
    this._queueStatusListeners.delete(listener);
  }

  // 移除所有队列状态监听器
  public removeAllListenerQueueStatus(): void {
    this._queueStatusListeners.clear();
  }

  // 添加上传成功监听器
  public addListenerUploadStatusSuccess(
    listener: (allFileUploadInfo: FileUploadInfo[]) => void,
  ): void {
    this._uploadSuccessListeners.add(listener);
  }

  // 移除上传成功监听器
  public removeListenerUploadStatusSuccess(
    listener: (allFileUploadInfo: FileUploadInfo[]) => void,
  ): void {
    this._uploadSuccessListeners.delete(listener);
  }

  // 移除所有上传成功监听器
  public removeAllListenerUploadStatusSuccess(): void {
    this._uploadSuccessListeners.clear();
  }

  // 私有方法：处理文件上传
  private async _uploadFileChunks(fileInfo: FileUploadInfo): Promise<void> {
    const file = fileInfo.file;
    const abortController = new AbortController();
    this._abortControllers.set(fileInfo.fileID, abortController);

    try {
      // 准备上传参数
      const params: Parameters<typeof FServiceAPI.Storage.uploadFile>[0] = {
        file: file,
      };

      // 保存 this 引用
      const self = this;

      // 调用上传API
      const [promise, cancel]: any = FServiceAPI.Storage.uploadFile(
        params,
        {
          onUploadProgress: (progressEvent: AxiosProgressEvent) => {
            const total = progressEvent.total ?? file.size;
            const progress: number = Math.floor(
              (progressEvent.loaded / total) * 100,
            );
            const oldProgress = fileInfo.progress;
            fileInfo.uploadedSize = progressEvent.loaded;
            fileInfo.progress = progress;
            fileInfo.totalSize = total;

            // 只在进度变化超过1%时通知
            if (Math.abs(progress - oldProgress) >= 1) {
              self._fileStatusChanges.set(fileInfo.fileID, { ...fileInfo });
              const changedFiles = Array.from(self._fileStatusChanges.values());
              self._queueStatusListeners.forEach((listener) => {
                // console.log(changedFiles, 'lichangedFilesstener########################');
                listener(changedFiles);
              });
              // console.log(
              //   self._queueStatusListeners,
              //   'self._queueStatusListeners########################',
              // );

              self._options.onProgress?.(self.getTotalProgress());
              self._options.onFileProgress?.(fileInfo.fileID, progress);
              self._fileStatusChanges.clear();
            }
          },
        },
        true,
      );

      // 设置取消函数
      abortController.signal.addEventListener('abort', () => {
        cancel();
      });

      // 等待上传完成
      const { data } = await promise;

      // 上传成功
      if (fileInfo.status === UploadStatus.UPLOADING) {
        fileInfo.status = UploadStatus.COMPLETED;
        fileInfo.progress = 100;
        fileInfo.uploadedSize = fileInfo.totalSize;
        this._fileStatusChanges.set(fileInfo.fileID, { ...fileInfo });
        this._options.onFileComplete?.(fileInfo.fileID);

        // 只通知完成的文件
        const changedFiles = Array.from(this._fileStatusChanges.values());
        this._queueStatusListeners.forEach((listener) =>
          listener(changedFiles),
        );
        this._uploadSuccessListeners.forEach((listener) =>
          listener(changedFiles),
        );
        this._fileStatusChanges.clear();
      }
    } catch (error) {
      if (
        fileInfo.status !== UploadStatus.PAUSED &&
        fileInfo.status !== UploadStatus.FAILED
      ) {
        await this._handleUploadError(fileInfo);
      }
    } finally {
      this._abortControllers.delete(fileInfo.fileID);
      this._activeUploads.delete(fileInfo.fileID);
      this._processQueue();
    }
  }

  // 处理上传错误，包括重试逻辑
  private async _handleUploadError(fileInfo: FileUploadInfo): Promise<void> {
    if (fileInfo.retryCount < this._options.maxRetries!) {
      fileInfo.retryCount++;
      fileInfo.status = UploadStatus.PENDING;
      this._fileStatusChanges.set(fileInfo.fileID, { ...fileInfo });
      await new Promise((resolve) =>
        setTimeout(resolve, this._options.retryDelay),
      );
      this._processQueue();
    } else {
      fileInfo.status = UploadStatus.FAILED;
      fileInfo.error = 'timeout';
      this._fileStatusChanges.set(fileInfo.fileID, { ...fileInfo });
      this._options.onFileError?.(fileInfo.fileID, 'timeout');
    }
  }

  // 处理上传队列，控制并发上传数量
  private _processQueue(): void {
    const pendingUploads = Array.from(this._uploadQueue.values()).filter(
      (fileInfo) => fileInfo.status === UploadStatus.PENDING,
    );

    while (
      this._activeUploads.size < this._options.maxConcurrentUploads! &&
      pendingUploads.length > 0
    ) {
      const fileInfo = pendingUploads.shift()!;
      fileInfo.status = UploadStatus.UPLOADING;
      this._activeUploads.add(fileInfo.fileID);
      this._uploadFileChunks(fileInfo);
    }
  }

  private notifyQueueStatusChange(): void {
    // Implementation of notifyQueueStatusChange method
  }
}

export default BatchFileUploadManager;
